import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import {
  X,
  CheckCircle,
  XCircle,
  MinusCircle,
  Zap,
  Clipboard,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { DetailedUser } from '../../types';
import { quickSetupUser, getRingCentralStatus, QuickSetupStep } from '../../services/api';

interface QuickSetupWizardProps {
  clientId: string;
  user: DetailedUser;
  isOpen: boolean;
  onClose: () => void;
  onComplete?: () => void;
}

function shortenLicenseName(name: string): string {
  return name
    .replace(/^Microsoft 365 /i, 'M365 ')
    .replace(/^Office 365 /i, 'O365 ')
    .replace(/^Enterprise Mobility \+ Security /i, 'EMS ')
    .replace(/^Azure Active Directory /i, 'AAD ')
    .replace(/^Azure AD /i, 'AAD ')
    .replace(/^Microsoft Defender for /i, 'Defender ')
    .replace(/^Microsoft /i, '')
    .replace(/^Dynamics 365 /i, 'D365 ');
}

export default function QuickSetupWizard({ clientId, user, isOpen, onClose, onComplete }: QuickSetupWizardProps) {
  const [phase, setPhase] = useState<'configure' | 'progress'>('configure');
  const [loading, setLoading] = useState(false);
  const [steps, setSteps] = useState<QuickSetupStep[]>([]);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);

  // Form state
  const [selectedLicense, setSelectedLicense] = useState('');
  const [sendCredentials, setSendCredentials] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');
  const [ringCentralConnected, setRingCentralConnected] = useState(false);

  // Check RingCentral status on open
  useEffect(() => {
    getRingCentralStatus()
      .then(r => setRingCentralConnected(r.connected))
      .catch(() => setRingCentralConnected(false));
  }, []);

  const handleExecute = async () => {
    setLoading(true);
    setPhase('progress');
    setSteps([]);
    setTemporaryPassword(null);

    try {
      const result = await quickSetupUser(clientId, user.id, {
        skuId: selectedLicense || undefined,
        sendCredentials,
        recipientEmail: sendCredentials ? recipientEmail : undefined,
        recipientPhone: sendCredentials && recipientPhone ? recipientPhone : undefined,
      });

      setSteps(result.steps);
      if (result.temporaryPassword) {
        setTemporaryPassword(result.temporaryPassword);
      }

      const failCount = result.steps.filter(s => s.status === 'failed').length;
      if (failCount === 0) {
        toast.success('Quick Setup completed successfully');
      } else {
        toast.error(`Quick Setup: ${failCount} step(s) failed`);
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Quick Setup failed');
      setSteps([{ name: 'Quick Setup', status: 'failed', message: err instanceof Error ? err.message : 'Unknown error' }]);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (phase === 'progress' && !loading) {
      onComplete?.();
    }
    setPhase('configure');
    setSteps([]);
    setTemporaryPassword(null);
    setSelectedLicense('');
    setSendCredentials(false);
    setRecipientEmail('');
    setRecipientPhone('');
    onClose();
  };

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  }

  const stepIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'skipped':
        return <MinusCircle className="h-5 w-5 text-muted-foreground" />;
      default:
        return <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="p-0 gap-0 max-w-lg [&>button]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-6 py-4 border-b border-blue-600 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Zap className="h-6 w-6 text-white mr-3" />
              <DialogTitle className="text-lg font-semibold text-white">
                Quick Setup
              </DialogTitle>
            </div>
            <DialogDescription className="sr-only">Enable account, assign license, reset password, and send credentials for a user</DialogDescription>
            <button
              onClick={handleClose}
              className="text-white/80 hover:text-white"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <p className="mt-1 text-sm text-blue-100">
            {user.displayName} ({user.userPrincipalName})
          </p>
        </div>

        {/* Content */}
        <div className="px-6 py-6 max-h-[60vh] overflow-y-auto">
          {phase === 'configure' ? (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Enable account, assign a license, reset password, and optionally send credentials — all in one click.
              </p>

              {/* Steps preview */}
              <div className="bg-muted rounded-lg p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">Steps to perform:</h4>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  <li className="flex items-center">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 text-xs font-medium mr-2">1</span>
                    {user.accountEnabled ? <span className="text-muted-foreground line-through">Enable account (already active)</span> : 'Enable account'}
                  </li>
                  <li className="flex items-center">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 text-xs font-medium mr-2">2</span>
                    Set usage location (US)
                  </li>
                  <li className="flex items-center">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 text-xs font-medium mr-2">3</span>
                    Assign license
                  </li>
                  <li className="flex items-center">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 text-xs font-medium mr-2">4</span>
                    Reset password
                  </li>
                  <li className="flex items-center">
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 text-xs font-medium mr-2">5</span>
                    Send credentials
                  </li>
                </ul>
              </div>

              {/* License selection */}
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-1">
                  License to assign
                </label>
                <select
                  value={selectedLicense}
                  onChange={e => setSelectedLicense(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                >
                  <option value="">None (skip license assignment)</option>
                  {user.availableLicenses.map(lic => (
                    <option key={lic.skuId} value={lic.skuId}>
                      {shortenLicenseName(lic.skuName)} ({lic.available} available)
                    </option>
                  ))}
                </select>
              </div>

              {/* Send credentials toggle */}
              <div>
                <label className="flex items-center space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendCredentials}
                    onChange={e => setSendCredentials(e.target.checked)}
                    className="h-4 w-4 text-blue-600 border-border rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-foreground">
                    Send credentials via email
                    {ringCentralConnected && <span className="ml-1 text-xs text-green-600">+ SMS</span>}
                  </span>
                </label>

                {sendCredentials && (
                  <div className="mt-3 space-y-3 pl-7">
                    <div>
                      <label className="block text-sm text-muted-foreground mb-1">
                        Recipient Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        value={recipientEmail}
                        onChange={e => setRecipientEmail(e.target.value)}
                        placeholder={user.userPrincipalName || 'email@example.com'}
                        className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    {ringCentralConnected && (
                      <div>
                        <label className="block text-sm text-muted-foreground mb-1">
                          Phone for SMS <span className="text-xs text-green-600">(SMS Ready)</span>
                        </label>
                        <input
                          type="tel"
                          value={recipientPhone}
                          onChange={e => setRecipientPhone(e.target.value)}
                          placeholder={user.mobilePhone || '+1 (555) 123-4567'}
                          className="w-full px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm focus:ring-2 focus:ring-blue-500"
                        />
                        <p className="mt-1 text-xs text-muted-foreground">Password via SMS, instructions via email</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Progress phase */
            <div className="space-y-4">
              {loading && steps.length === 0 && (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-sm text-muted-foreground">Running Quick Setup...</p>
                </div>
              )}

              {steps.length > 0 && (
                <div className="space-y-3">
                  {steps.map((step, i) => (
                    <div key={i} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
                      <div className="flex-shrink-0 mt-0.5">
                        {stepIcon(step.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-foreground">{step.name}</span>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            step.status === 'success' ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' :
                            step.status === 'failed' ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' :
                            'bg-muted text-muted-foreground'
                          }`}>
                            {step.status}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">{step.message}</p>
                        {step.error && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-1">{step.error}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Password display */}
              {temporaryPassword && !loading && (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                    Temporary Password (user must change on next sign-in):
                  </p>
                  <div className="flex items-center space-x-2">
                    <code className="flex-1 px-3 py-2 bg-card border border-border rounded font-mono text-sm text-foreground">
                      {temporaryPassword}
                    </code>
                    <button
                      onClick={() => copyToClipboard(temporaryPassword)}
                      className="p-2 text-muted-foreground hover:text-foreground"
                      title="Copy to clipboard"
                    >
                      <Clipboard className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              )}

              {/* Summary */}
              {!loading && steps.length > 0 && (
                <div className={`p-3 rounded-lg text-center ${
                  steps.every(s => s.status !== 'failed')
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                    : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                }`}>
                  <p className="text-sm font-medium">
                    {steps.filter(s => s.status === 'success').length} succeeded,{' '}
                    {steps.filter(s => s.status === 'skipped').length} skipped,{' '}
                    {steps.filter(s => s.status === 'failed').length} failed
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-muted px-6 py-4 border-t border-border rounded-b-lg">
          <div className="flex justify-end space-x-3">
            {phase === 'configure' ? (
              <>
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-muted-foreground bg-card border border-border rounded-md hover:bg-accent"
                >
                  Cancel
                </button>
                <button
                  onClick={handleExecute}
                  disabled={loading || (sendCredentials && !recipientEmail)}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <Zap className="h-4 w-4 mr-1" />
                  Run Quick Setup
                </button>
              </>
            ) : (
              <button
                onClick={handleClose}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Running...' : 'Done'}
              </button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
